import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CompaniesPage from './pages/CompaniesPage';
import CreateCompanyPage from './pages/CreateCompanyPage';
import CompanyWorkspace from './pages/CompanyWorkspace';
import CompanyOverview from './pages/CompanyOverview';
import CompanyAccounts from './pages/CompanyAccounts';
import CompanyPosts from './pages/CompanyPosts';
import PostEditor from './pages/PostEditor';
import CompanyCalendar from './pages/CompanyCalendar';
import CompanyMedia from './pages/CompanyMedia';
import CompanySettings from './pages/CompanySettings';
import CompanyAI from './pages/CompanyAI';
import AdminSettings from './pages/AdminSettings';
import IntegrationsPage from './pages/IntegrationsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><CompaniesPage /></ProtectedRoute>} />
      <Route path="/companies/new" element={<ProtectedRoute><CreateCompanyPage /></ProtectedRoute>} />
      <Route path="/companies/:companyId" element={<ProtectedRoute><CompanyWorkspace /></ProtectedRoute>}>
        <Route index element={<CompanyOverview />} />
        <Route path="accounts" element={<CompanyAccounts />} />
        <Route path="posts" element={<CompanyPosts />} />
        <Route path="posts/new" element={<PostEditor />} />
        <Route path="posts/:postId/edit" element={<PostEditor />} />
        <Route path="calendar" element={<CompanyCalendar />} />
        <Route path="media" element={<CompanyMedia />} />
        <Route path="ai" element={<CompanyAI />} />
        <Route path="settings" element={<CompanySettings />} />
      </Route>
      <Route path="/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
